import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { LOGO_URL, SOCIAL_LINKS } from "@/lib/rn3";
import { useAuth } from "@/lib/auth";
import { useT, LanguageToggle } from "@/lib/i18n";
import { Menu, X, Facebook, Instagram, Youtube, Linkedin, Music2, Mail, Phone, MapPin, LogOut, ShieldCheck } from "lucide-react";

const NAV = [
  { to: "/", key: "nav.home" },
  { to: "/about", key: "nav.about" },
  { to: "/services", key: "nav.services" },
  { to: "/appointments", key: "nav.appointments" },
  { to: "/intake", key: "nav.intake" },
  { to: "/portal", key: "nav.portal" },
  { to: "/resources", key: "nav.resources" },
  { to: "/contact", key: "nav.contact" },
];

const ICONS = { facebook: Facebook, instagram: Instagram, music: Music2, youtube: Youtube, linkedin: Linkedin };

export const SocialIcons = ({ className = "", invert = false }) => (
  <div className={`flex items-center gap-3 ${className}`}>
    {SOCIAL_LINKS.map((s) => {
      const Icon = ICONS[s.icon] || Facebook;
      return (
        <a
          key={s.name}
          href={s.href}
          target="_blank"
          rel="noreferrer"
          aria-label={s.name}
          data-testid={`social-${s.icon}`}
          className={`w-9 h-9 inline-flex items-center justify-center border rounded-full transition-colors ${
            invert
              ? "border-white/30 text-white hover:border-[#C4A252] hover:text-[#C4A252]"
              : "border-[#E5E5E5] text-[#404040] hover:border-[#C4A252] hover:text-[#8B1C22]"
          }`}
        >
          <Icon className="w-4 h-4" />
        </a>
      );
    })}
  </div>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const t = useT();

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-xl border-b border-[#E5E5E5]" data-testid="site-header">
      <div className="rn3-container flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <img src={LOGO_URL} alt="Roman Numerals III" width="48" height="48" decoding="async" fetchpriority="high" className="h-12 w-12 object-contain" />
          <div className="leading-tight hidden sm:block">
            <div className="font-serif-display text-lg text-[#0A0A0A]">Roman Numerals III</div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#737373]">{t("footer.tagline")}</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.key.replace(/[.\s]+/g, "-").toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide transition-colors ${
                  isActive ? "text-[#8B1C22] rn3-red-underline" : "text-[#0A0A0A] hover:text-[#8B1C22]"
                }`
              }
              end={n.to === "/"}
            >
              {t(n.key)}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageToggle />
          {user ? (
            <>
              {user.role === "admin" && (
                <button
                  onClick={() => nav("/admin")}
                  data-testid="header-admin-btn"
                  className="text-sm inline-flex items-center gap-1.5 text-[#8B1C22] hover:underline"
                >
                  <ShieldCheck className="w-4 h-4" /> {t("nav.admin")}
                </button>
              )}
              <span className="text-xs text-[#737373] hidden xl:inline" data-testid="header-user-name">
                {user.full_name?.split(" ")[0]}
              </span>
              <button
                onClick={() => { logout(); nav("/"); }}
                data-testid="header-logout-btn"
                className="text-sm inline-flex items-center gap-1.5 text-[#404040] hover:text-[#8B1C22]"
              >
                <LogOut className="w-4 h-4" /> {t("nav.sign_out")}
              </button>
            </>
          ) : null}
        </div>

        <button
          className="lg:hidden p-2 -mr-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-btn"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#E5E5E5] bg-white" data-testid="mobile-menu">
          <div className="rn3-container py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`mnav-${n.key.replace(/[.\s]+/g, "-").toLowerCase()}`}
                className={({ isActive }) =>
                  `py-3 px-2 text-base ${isActive ? "text-[#8B1C22] font-semibold" : "text-[#0A0A0A]"}`
                }
                end={n.to === "/"}
              >
                {t(n.key)}
              </NavLink>
            ))}
            <div className="border-t border-[#E5E5E5] mt-3 pt-3 flex items-center justify-between">
              <LanguageToggle />
              {user ? (
                <button onClick={() => { logout(); nav("/"); setOpen(false); }} className="text-sm" data-testid="mobile-logout-btn">{t("nav.sign_out")}</button>
              ) : (
                <Link to="/portal" onClick={() => setOpen(false)} className="text-sm" data-testid="mobile-login-link">{t("nav.sign_in")}</Link>
              )}
              <SocialIcons />
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export const Footer = () => {
  const t = useT();
  return (
  <footer className="bg-[#0A0A0A] text-white" data-testid="site-footer">
    <div className="rn3-container py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
      <div className="md:col-span-2">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Roman Numerals III" width="48" height="48" loading="lazy" decoding="async" className="h-12 w-12 object-contain bg-white p-1 rounded-sm" />
          <div className="font-serif-display text-2xl">Roman Numerals III</div>
        </div>
        <p className="mt-6 text-[#bdbdbd] max-w-md leading-relaxed text-sm">
          {t("footer.about")}
        </p>
        <div className="mt-6 space-y-2 text-sm text-[#bdbdbd]">
          <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#C4A252]" /> info@romannumeralsiii.com</div>
          <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#C4A252]" /> {t("footer.call_to_schedule")}</div>
          <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#C4A252]" /> {t("footer.bilingual_support")}</div>
        </div>
        <div className="mt-6">
          <SocialIcons invert />
        </div>
      </div>
      <div>
        <div className="rn3-eyebrow text-[#C4A252]">{t("footer.navigate")}</div>
        <ul className="mt-4 space-y-2 text-sm">
          {NAV.slice(0, 6).map((n) => (
            <li key={n.to}><Link to={n.to} className="text-[#bdbdbd] hover:text-white transition-colors">{t(n.key)}</Link></li>
          ))}
        </ul>
      </div>
      <div>
        <div className="rn3-eyebrow text-[#C4A252]">{t("footer.legal")}</div>
        <ul className="mt-4 space-y-2 text-sm">
          <li><Link to="/privacy" className="text-[#bdbdbd] hover:text-white">{t("footer.privacy")}</Link></li>
          <li><Link to="/terms" className="text-[#bdbdbd] hover:text-white">{t("footer.terms")}</Link></li>
          <li><Link to="/welcome" className="text-[#bdbdbd] hover:text-white">{t("footer.qr_welcome")}</Link></li>
        </ul>
        <div className="mt-8 text-xs text-[#737373]">© {new Date().getFullYear()} Roman Numerals III. {t("footer.copyright")}</div>
        <div className="mt-2 text-xs text-[#737373]" data-testid="footer-powered-by">
          {t("footer.powered_by")}{" "}
          <a
            href="https://techtronicsmedia.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#C4A252] hover:text-white transition-colors underline-offset-2 hover:underline"
            data-testid="footer-techtronics-link"
          >
            ADN, a division of TechTronics Media
          </a>
        </div>
      </div>
    </div>
  </footer>
  );
};

import HablaEspanolBanner from "@/components/HablaEspanolBanner";
import AdelisAI from "@/components/AdelisAI";
import GlobalSearch from "@/components/GlobalSearch";

export const Layout = ({ children }) => (
  <div className="min-h-screen flex flex-col bg-white">
    {/* Accessibility: skip-to-content link for keyboard + screen-reader users */}
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:bg-[#8B1C22] focus:text-white focus:px-4 focus:py-2 focus:rounded focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#C4A252]"
      data-testid="skip-to-content"
    >
      Skip to main content
    </a>
    <Header />
    <main id="main-content" className="flex-1" tabIndex={-1}>{children}</main>
    <Footer />
    <HablaEspanolBanner />
    <AdelisAI />
    <GlobalSearch />
  </div>
);
